import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Skeleton,
    Alert,
    Dialog,
    DialogContent,
    IconButton,
    Chip
} from '@mui/material';
import axios from 'axios';
import MemoryIcon from '@mui/icons-material/Memory';
import SpeedIcon from '@mui/icons-material/Speed';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { AutoAwesome } from '@mui/icons-material';
import { CustomTooltip } from '../Settings/Organization';
import { CONFIG_APP_NIFE_AI_METRICS } from '../../configApp';

const ForecastResult = ({ orgId, appName }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [openImage, setOpenImage] = useState({ open: false, src: '', title: '' });

    useEffect(() => {
        const fetchForecast = async () => {
            setLoading(true);
            try {
                // Determine API endpoint based on presence of appName
                const endpoint = appName
                    ? `${CONFIG_APP_NIFE_AI_METRICS}/api/forecast/app/${orgId}/${appName}`
                    : `${CONFIG_APP_NIFE_AI_METRICS}/api/forecast/namespace/${orgId}`;

                const res = await axios.get(endpoint);
                setData(res.data);
                setError('');
            } catch (err) {
                console.error(err);
                setError('Failed to fetch forecast data.');
            } finally {
                setLoading(false);
            }
        };

        if (orgId) {
            fetchForecast();
        }
    }, [orgId, appName]);

    const handleOpenImage = (src, title) => {
        setOpenImage({ open: true, src, title });
    };

    const handleCloseImage = () => {
        setOpenImage({ open: false, src: '', title: '' });
    };

    const metricCards = data
        ? [
            // {
            //     title: 'CPU MAE',
            //     value: data.cpu_mae,
            //     desc: 'Mean Absolute Error of CPU usage forecast.',
            //     icon: <SpeedIcon color="primary" />,
            // },
            // {
            //     title: 'CPU RMSE',
            //     value: data.cpu_rmse,
            //     desc: 'Root Mean Squared Error of CPU usage forecast.',
            //     icon: <SpeedIcon color="primary" />,
            // },
            // {
            //     title: 'CPU Threshold',
            //     value: data.cpu_threshold,
            //     desc: 'Configured CPU alert threshold.',
            //     icon: <SpeedIcon color="primary" />,
            // },
            {
                title: 'Max CPU',
                value: data.max_cpu,
                desc: 'Maximum observed CPU usage.',
                icon: <SpeedIcon color="primary" />,
            },
            // {
            //     title: 'Memory MAE',
            //     value: data.mem_mae,
            //     desc: 'Mean Absolute Error of Memory usage forecast.',
            //     icon: <MemoryIcon color="secondary" />,
            // },
            // {
            //     title: 'Memory RMSE',
            //     value: data.mem_rmse,
            //     desc: 'Root Mean Squared Error of Memory usage forecast.',
            //     icon: <MemoryIcon color="secondary" />,
            // },
            // {
            //     title: 'Memory Threshold',
            //     value: data.mem_threshold,
            //     desc: 'Configured Memory alert threshold.',
            //     icon: <MemoryIcon color="secondary" />,
            // },
            {
                title: 'Max Memory',
                value: data.max_mem,
                desc: 'Maximum observed Memory usage.',
                icon: <MemoryIcon color="secondary" />,
            },
        ]
        : Array(2).fill(null); // Skeleton placeholder

    if (error) {
        return (
            <Box my={2} mb={4}>
                <Typography variant="h5" sx={{ pb: 2 }} component="h1">
                    Resource Usage Forecast (BETA)                     <CustomTooltip title="Nia is Nife’s AI-powered assistant designed to forecast CPU and memory usage, providing actionable insights to plan, scale, and optimize your applications proactively." arrow>
                        <Chip sx={{ ml: 2 }} variant='outlined' label={loading ? "Nia is thinking..." : "Nia AI"} size='small' icon={<AutoAwesome style={{ fontSize: "1rem" }} />} />
                    </CustomTooltip>
                </Typography>
                <Alert sx={{ mb: 2 }} severity="info">Not enough data to show forecast for this {appName ? "application" : "organization"}.</Alert>
            </Box>
        );
    }

    return (
        <Box mt={2} mb={4}>
            <Typography variant="h5" sx={{ pb: 2 }} component="h1">
                Resource Usage Forecast (BETA) {appName && ` - ${appName}`}
                <CustomTooltip title="Nia is Nife’s AI-powered assistant designed to forecast CPU and memory usage, providing actionable insights to plan, scale, and optimize your applications proactively." arrow>
                    <Chip sx={{ ml: 2 }} variant='outlined' label={loading ? "Nia is thinking..." : "Nia AI"} size='small' icon={<AutoAwesome style={{ fontSize: "1rem" }} />} />
                </CustomTooltip>
            </Typography>

            <Grid container spacing={2} sx={{ mb: 4 }}>
                {metricCards.map((card, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                        <Card>
                            <CardContent>
                                {loading ? (
                                    <>
                                        <Skeleton variant="rectangular" width={40} height={40} sx={{ mb: 1 }} />
                                        <Skeleton variant="text" width="60%" />
                                        <Skeleton variant="text" width="80%" />
                                        <Skeleton variant="text" width="100%" />
                                    </>
                                ) : (
                                    <>
                                        <Box display="flex" alignItems="center" mb={1}>
                                            {card.icon}
                                            <Typography variant="h6" sx={{ fontSize: '1rem' }} ml={1}>
                                                {card.title}
                                            </Typography>
                                        </Box>
                                        <Typography variant="h5" fontWeight="bold">
                                            {card.value}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            {card.desc}
                                        </Typography>
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Grid container spacing={2}>
                {['Actual Usage', 'Forecast Usage'].map((plotTitle, idx) => {
                    const imageSrc = data
                        ? `data:image/png;base64,${idx === 0 ? data.actual_plot : data.forecast_plot}`
                        : '';
                    const desc = idx === 0 ? 'Data for past 30 minutes' : 'Forecast for next 30 minutes';

                    return (
                        <Grid item xs={12} md={6} key={idx}>
                            <Card>
                                <CardContent>
                                    {loading ? (
                                        <>
                                            <Skeleton variant="text" width="50%" />
                                            <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
                                        </>
                                    ) : (
                                        <>
                                            <Box display="flex" alignItems="center" mb={1} justifyContent="space-between">
                                                <Box>
                                                    <Typography variant="h6">{plotTitle}</Typography>
                                                    <Typography variant="body2" color="textSecondary">{desc}</Typography>
                                                </Box>
                                                <IconButton onClick={() => handleOpenImage(imageSrc, plotTitle)}>
                                                    <OpenInFullIcon />
                                                </IconButton>
                                            </Box>
                                            <Box
                                                component="img"
                                                src={imageSrc}
                                                alt={plotTitle}
                                                sx={{ width: '100%', borderRadius: 1, cursor: 'pointer' }}
                                                onClick={() => handleOpenImage(imageSrc, plotTitle)}
                                            />
                                        </>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            <Dialog open={openImage.open} onClose={handleCloseImage} maxWidth="lg" fullWidth>
                <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
                    <Typography variant="h6">{openImage.title}</Typography>
                    <IconButton onClick={handleCloseImage}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <DialogContent>
                    <Box
                        component="img"
                        src={openImage.src}
                        alt={openImage.title}
                        sx={{ width: '100%', borderRadius: 1, objectFit: "cover" }}
                    />
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default ForecastResult;
